import React, { Component, ReactNode } from 'react';
import { withRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Environment } from 'src/Environment';
import { TechnicalErrorSelfServiceProvider } from 'src/context/technical-error-self-service-context';
import TealiumEvents from 'src/helpers/tealium-events';
import { TechnicalErrorIceCream, LeftArrowWhite, CopyToClipboard } from 'src/assets/images';
import { Tooltip } from 'src/components';
import { v4 } from 'uuid';
import HeaderNav from '../header-nav/header-nav';
import { vfsHomeUrl } from 'src/common';
import { getJourneyNameFromSessionStorage } from 'src/utils/SessionStorage/GetJourneyNameFromSessionStorage';
import { SENTRY_TAG_KEYS } from 'src/types';
import './technical-error-self-service.scss';

interface StateType {
  hasError: boolean;
  fullPage: boolean;
  uuid?: string;
}

type PropsType = {
  hasError: boolean;
  fullPage: boolean;
  children: ReactNode;
  manualFailure?: string;
};

type ManualFailureMessages = {
  [key: string]: string;
};

const MANUAL_FAILURE_MESSAGES: ManualFailureMessages = {
  transactions: 'Recent transaction call failed on payment services dashboard',
  'account-balance': 'Account balance call failed on payment services dashboard',
  'transaction-data': 'Transaction data call failed on payment services dashboard',
};

class TechnicalErrorSelfService extends Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);
    this.state = { hasError: props?.hasError, fullPage: props?.fullPage };
  }

  static getDerivedStateFromError(error: Error) {
    const uuid = v4().replace(/-/g, '');

    TealiumEvents.onTechnicalErrorPageLoadSelfServiceIssue('error occurred during page render');
    if (!Environment?.current?.disabledFeatures?.sentry) {
      Sentry.captureMessage(`${uuid}: ${error.message}`, 'error');
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, getJourneyNameFromSessionStorage());
    }

    return { hasError: true, uuid };
  }

  componentDidMount() {
    if (this.props.manualFailure) {
      const uuid = v4().replace(/-/g, '');
      if (!Environment?.current?.disabledFeatures?.sentry) {
        Sentry.captureMessage(
          `${uuid}: ${MANUAL_FAILURE_MESSAGES[this.props.manualFailure]}`,
          'error',
        );
        Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, getJourneyNameFromSessionStorage());
      }
      this.setState({ uuid: uuid });
    }
  }

  /*
  isEvent (default = false)
  Used to determine the type of tealium event that is logged.
  true if the error is in response to an event like an API call.
  false if the error happens on page load
  */
  handleTechnicalError = (errorMessage, isEvent = false, suppressTealiumEvent = false) => {
    if (this.state.hasError) {
      return;
    }

    if (!suppressTealiumEvent) {
      if (isEvent) {
        TealiumEvents.onTechnicalErrorUserEventTriggeredSelfServiceIssue(errorMessage);
      } else {
        TealiumEvents.onTechnicalErrorPageLoadSelfServiceIssue(errorMessage);
      }
    }

    const uuid = v4().replace(/-/g, '');
    this.setState({ hasError: true, uuid: uuid });

    const error = new Error(errorMessage);

    if (!Environment?.current?.disabledFeatures?.sentry) {
      Sentry.captureMessage(`${uuid}: ${error.message}`, 'error');
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, getJourneyNameFromSessionStorage());
    }

    throw error;
  };

  copyUuidToClipboard = () => navigator.clipboard.writeText(this.state.uuid || '');

  render() {
    if (this.state.hasError) {
      return (
        <>
          {this.state.fullPage ? (
            <HeaderNav>
              <section className="technical-error-self-service full-page">
                <img src={TechnicalErrorIceCream} />
                <p className="technical-error-heading heading-five">
                  We are experiencing technical difficulties
                </p>

                <div className="uuid-block">
                  <p>{this.state.uuid}</p>

                  {/* @ts-ignore */}
                  <Tooltip content="Copied!" showOnClick copyToClipboard>
                    <img src={CopyToClipboard} alt="Copy" onClick={this.copyUuidToClipboard} />
                  </Tooltip>
                </div>

                <a className="content-normal" href={vfsHomeUrl} target="_self">
                  <img className="technical-error-arrow" src={LeftArrowWhite} /> Back to
                  vodacombusiness.co.za
                </a>
              </section>
            </HeaderNav>
          ) : (
            <section className="technical-error-self-service">
              <img src={TechnicalErrorIceCream} />
              <p className="technical-error-heading heading-five">
                We are experiencing technical difficulties
              </p>

              <div className="uuid-block">
                <p>{this.state.uuid}</p>

                {/* @ts-ignore */}
                <Tooltip content="Copied!" showOnClick copyToClipboard>
                  <img src={CopyToClipboard} alt="Copy" onClick={this.copyUuidToClipboard} />
                </Tooltip>
              </div>

              <a className="content-normal" href={vfsHomeUrl} target="_self">
                <img className="technical-error-arrow" src={LeftArrowWhite} /> Back to
                vodacombusiness.co.za
              </a>
            </section>
          )}
        </>
      );
    }

    return (
      <TechnicalErrorSelfServiceProvider
        handleTechnicalError={(errorMessage, isEvent, suppressTealiumEvent) =>
          this.handleTechnicalError(errorMessage, isEvent, suppressTealiumEvent)
        }
      >
        {this.props.children}
      </TechnicalErrorSelfServiceProvider>
    );
  }
}

export default withRouter(TechnicalErrorSelfService);
